import { documentScope, FRAME_SANDBOX } from '../../contracts/runtime-v2.mjs';

// A saved chat-built App runs on its own origin (app-<id>), which is its wall:
// same-origin there gives it workers, storage and its own cache; downloads and
// dialogs work. It can never reach Chat's origin. Popups stay off: an opened
// window is an unguarded way out for data the App holds (links to other sites
// wait for the per-App allowlist the owner approves).
export const SAVED_APP_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-downloads allow-modals';
import { createRuntimeTransport } from './transport.mjs';
import { createOpaquePreviewTransport } from './opaque-preview-transport.mjs';
import { focusReadyFrame } from './frame-focus.mjs';

function validateMount(window, iframe, issued, scope) {
  if (
    window.location.origin !== scope.hostOrigin ||
    issued.bootstrapUrl !== `${scope.appOrigin}/_bootstrap` ||
    !/^[A-Za-z0-9_-]{43}$/.test(issued.ticket)
  )
    throw new Error('runtime_mount_rejected');
  iframe.setAttribute('sandbox', issued.privateChatApp ? SAVED_APP_SANDBOX : FRAME_SANDBOX);
  // A chat-built deck presents full screen from inside its frame (the browser shows its own exit hint).
  if (issued.privateChatApp && issued.deck) iframe.setAttribute('allow', 'fullscreen *');
  iframe.setAttribute('referrerpolicy', 'no-referrer');
  iframe.name = `poneva-${scope.documentNonce}`;
  if (!iframe.contentWindow) throw new Error('runtime_frame_unmounted');
  if (iframe.getAttribute('src') && iframe.getAttribute('src') !== 'about:blank')
    throw new Error('fresh_runtime_frame_required');
  iframe.contentWindow.name = iframe.name;
}

// UI1 provides the frame markup. RUN owns transport and lifecycle retirement;
// INS/DES2 register inspect operations. Navigation recovery never replays calls.
export function mountNamedRuntime({
  window,
  iframe,
  issued,
  handlers,
  renew,
  retire,
  onInvalidated,
}) {
  const scope = documentScope(issued.scope);
  validateMount(window, iframe, issued, scope);
  if (![renew, retire, onInvalidated].every((fn) => typeof fn === 'function'))
    throw new TypeError('runtime_lifecycle_adapters_required');
  let mounted = false;
  let form = null;
  let input = null;
  const clearForm = () => {
    if (input) input.value = '';
    form?.remove();
  };
  const trustedReady = handlers?.['runtime.ready']?.request;
  if (typeof trustedReady !== 'function') throw new TypeError('runtime_ready_handler_required');
  const transport = (issued.privateChatApp ? createOpaquePreviewTransport : createRuntimeTransport)(
    {
      window,
      peer: iframe.contentWindow,
      scope,
      // The saved App's port handshake comes from its own origin.
      frameOrigin: scope.appOrigin,
      side: 'host',
      handlers: {
        ...handlers,
        'runtime.ready': {
          request: async (args, options) => {
            const result = await trustedReady(args, options);
            if (!retired && !mounted) {
              mounted = true;
              clearForm();
              clearTimeout(timer);
              timer = setTimeout(refresh, 240000);
              focusReadyFrame(iframe, window.document);
            }
            return result;
          },
        },
      },
    },
  );
  let retired = false;
  let initialPrivateLoad = issued.privateChatApp === true;
  let timer;
  const invalidate = () => {
    if (retired) return;
    retired = true;
    clearTimeout(timer);
    clearForm();
    iframe.removeEventListener('load', loaded);
    transport.invalidate();
    void Promise.resolve()
      .then(() => retire(scope))
      .catch(() => {});
    onInvalidated();
  };
  const refresh = async () => {
    try {
      await renew(scope);
      if (!retired) timer = setTimeout(refresh, 240000);
    } catch {
      invalidate();
    }
  };
  const loaded = () => {
    // The opaque bridge can report ready before the parent sees the same
    // initial load. That load belongs to this ticket; later navigation retires.
    if (initialPrivateLoad) {
      initialPrivateLoad = false;
      return;
    }
    if (mounted) invalidate();
  };
  iframe.addEventListener('load', loaded);
  transport.onInvalidate(invalidate);
  form = window.document.createElement('form');
  input = window.document.createElement('input');
  form.method = 'POST';
  form.action = issued.bootstrapUrl;
  form.target = iframe.name;
  form.hidden = true;
  input.type = 'hidden';
  input.name = 'ticket';
  input.value = issued.ticket;
  form.append(input);
  window.document.body.append(form);
  try {
    form.submit();
    timer = setTimeout(invalidate, 30000);
  } catch (error) {
    invalidate();
    throw error;
  }
  return Object.freeze({ transport, scope, invalidate });
}
